import { useCallback, useState } from 'react';
import type { StatsBucketDto, StatsDto, StatsPeriod } from '../../api/cabinet';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import type { UiKey } from '../../i18n/dict';
import { Chip } from '../../ui/controls';
import { ErrorState, Loader } from '../../ui/feedback';
import { RatingLine } from '../../ui/rating';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { Card } from './profile/fields';

const PERIODS: readonly [StatsPeriod, UiKey][] = [
  ['day', 'statsDay'],
  ['week', 'statsWeek'],
  ['month', 'statsMonth'],
];

/** Подпись столбца: день и неделя — «16.09», месяц — «сен». Дата уже в поясе организации. */
function bucketLabel(start: string, period: StatsPeriod, lang: string): string {
  const [y, m, d] = start.split('-').map(Number) as [number, number, number];
  if (period !== 'month') return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang === 'uz' ? 'uz-Latn' : 'ru', {
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * `load` переопределяет источник данных: по умолчанию статистика кабинета владельца, но
 * админка платформы передаёт свой загрузчик (`api.admin.businessStats`), чтобы тем же экраном
 * смотреть любой бизнес. Форма ответа одна (`StatsDto`), поэтому отрисовка не меняется.
 */
export function StatsScreen(props: {
  businessId: string;
  onBack: () => void;
  load?: (period: StatsPeriod) => Promise<StatsDto>;
}): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const [period, setPeriod] = useState<StatsPeriod>('day');

  const load = props.load ?? ((p: StatsPeriod) => api.cabinet.getStats(props.businessId, p));
  const { state, reload } = useAsync(
    useCallback(() => load(period), [load, period]),
    [props.businessId, period],
  );

  return (
    <Screen>
      <TopBar title={t('statsTitle')} onBack={props.onBack} />
      <ScrollArea bottomGap="tabs">
        <div className="flex gap-2 px-4 pt-4">
          {PERIODS.map(([value, key]) => (
            <Chip key={value} active={value === period} onClick={() => setPeriod(value)}>
              {t(key)}
            </Chip>
          ))}
        </div>

        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {state.status === 'ready' ? (
          <>
            <div className="px-4 pt-5">
              <SectionTitle>{t('statsRating')}</SectionTitle>
              <Card>
                <RatingLine sum={state.data.rating.sum} count={state.data.rating.count} />
              </Card>
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>{t('statsCurrent')}</SectionTitle>
              <Summary bucket={state.data.buckets[state.data.buckets.length - 1]} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>{t('statsVisits')}</SectionTitle>
              <Bars buckets={state.data.buckets} period={period} lang={lang} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>{t('statsTotalRange')}</SectionTitle>
              <Summary bucket={{ ...sumBuckets(state.data.buckets), clients: state.data.clients.clients }} />
            </div>

            <div className="px-4 pt-5">
              <SectionTitle>
                {t('statsRegulars')} · {state.data.clients.regulars}
              </SectionTitle>
              <Card>
                {state.data.clients.top.length === 0 ? (
                  <div className="py-1 text-[13px] text-ink-3">{t('statsNoRegulars')}</div>
                ) : (
                  state.data.clients.top.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-1.5 text-[14px]">
                      <span className="truncate font-semibold">{c.name}</span>
                      <span className="shrink-0 text-ink-2">
                        {c.visits} {t('statsVisitsCount')}
                      </span>
                    </div>
                  ))
                )}
              </Card>
            </div>
          </>
        ) : null}
      </ScrollArea>
    </Screen>
  );
}

/** Итог записей за диапазон. Клиентов суммировать нельзя — их даёт сервер (`clients`). */
function sumBuckets(buckets: readonly StatsBucketDto[]): Omit<StatsBucketDto, 'start' | 'clients'> {
  return buckets.reduce(
    (acc, b) => ({
      total: acc.total + b.total,
      completed: acc.completed + b.completed,
      noShow: acc.noShow + b.noShow,
      cancelled: acc.cancelled + b.cancelled,
    }),
    { total: 0, completed: 0, noShow: 0, cancelled: 0 },
  );
}

function Summary(props: { bucket: Omit<StatsBucketDto, 'start'> | undefined }): JSX.Element | null {
  const { t } = useI18n();
  const b = props.bucket;
  if (!b) return null;
  const cells: readonly [UiKey, number][] = [
    ['statsVisits', b.total],
    ['statsClients', b.clients],
    ['statsCompleted', b.completed],
    ['statsNoShow', b.noShow],
    ['statsCancelled', b.cancelled],
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {cells.map(([key, value]) => (
        <div key={key} className="rounded-card bg-surface px-3 py-3 shadow-[inset_0_0_0_1px_var(--hair)]">
          <div className="text-[22px] font-extrabold tracking-[-0.02em]">{value}</div>
          <div className="mt-0.5 text-[11.5px] text-ink-3">{t(key)}</div>
        </div>
      ))}
    </div>
  );
}

function Bars(props: { buckets: readonly StatsBucketDto[]; period: StatsPeriod; lang: string }): JSX.Element {
  const max = Math.max(1, ...props.buckets.map((b) => b.total));
  return (
    <div className="rounded-card bg-surface px-2 pb-2 pt-3 shadow-[inset_0_0_0_1px_var(--hair)]">
      <div className="flex h-[140px] items-end gap-1">
        {props.buckets.map((b) => (
          <div key={b.start} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
            <div className="text-[10px] font-semibold text-ink-2">{b.total > 0 ? b.total : ''}</div>
            <div
              className="w-full rounded-t-[4px] bg-accent"
              // Минус место под цифру над столбцом, иначе самый высокий вылез бы за карточку.
              style={{
                height: `calc((100% - 16px) * ${b.total / max})`,
                minHeight: b.total > 0 ? 3 : 1,
                opacity: b.total > 0 ? 1 : 0.25,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        {props.buckets.map((b, i) => (
          <div key={b.start} className="flex-1 truncate text-center text-[9px] text-ink-3">
            {/* На 14 днях подписи не влезают все — показываем через одну. */}
            {props.buckets.length > 12 && i % 2 === 1 ? '' : bucketLabel(b.start, props.period, props.lang)}
          </div>
        ))}
      </div>
    </div>
  );
}
